<script setup lang="ts">
import {
    UserAvatar,
    UserAccountSummary,
    CurrentTeamSelector,
} from '@precon-tools-suite-by-shared/shared-auth-ui';
import {
    useIsAuthenticated,
    useLogin,
    useLogout,
    useProfileEdit,
} from '@precon-tools-suite-by-shared/vue-msal';
import { AppTopbarItem } from '@precon-tools-suite-by-shared/shared-layout-freya-ui';
import { ref, computed, watch } from 'vue';
import LoginButton from './LoginButton.vue';
import { useAuthentication } from '../composables/useAuthentication';
import { useAuthorization } from '../composables/useAuthorization';
import { useLazyQuery } from '@vue/apollo-composable';
import { useConnection } from '@precon-tools-suite-by-shared/shared-graphql-client-vue-pagination';
import { CurrentUserTeamsDocument } from './PreconTopbarUserProfile.generated';
import { useI18n } from 'vue-i18n';
import TeamBadge from './TeamBadge.vue';

const isAuthenticated = useIsAuthenticated();

const { t } = useI18n();

// ---------------------------------------------------------------------------------------------------------- //
//  ACCOUNT INFO LOADING                                                                                      //
// ---------------------------------------------------------------------------------------------------------- //

const {
    name,
    email,
    idTokenClaims,
    loading: accountInfoLoading,
} = useAuthentication();
const { roles, loading: authorizationLoading } = useAuthorization();
const { logout, loading: logoutLoading } = useLogout();
const { profileEdit, disabled: profileEditDisabled } = useProfileEdit();
const { login } = useLogin();

const { result, loading, load } = useLazyQuery(
    CurrentUserTeamsDocument,
    {},
    () => ({
        context: { noAuth: false },
    })
);

watch(
    isAuthenticated,
    (authenticated) => {
        if (authenticated) {
            load();
        }
    },
    { immediate: true }
);

const { nodes } = useConnection(() => result?.value?.me?.user?.teams);

const teams = computed(() =>
    loading.value
        ? null
        : nodes.value.map((node) => ({
              id: node.uuid,
              name: node.name,
          }))
);

const currentTeamId = computed(() => idTokenClaims.value?.team ?? null);
const switchingTeamId = ref<string | null>(currentTeamId.value);

const currentTeamName = computed(() => {
    const currentTeam = nodes.value.find(
        (node) => node.uuid === currentTeamId.value
    );
    return currentTeam?.name?.full || '';
});

watch(currentTeamId, () => (switchingTeamId.value = currentTeamId.value));
watch(switchingTeamId, (value) => {
    if (!!value && value !== currentTeamId.value) {
        login({
            extraQueryParameters: {
                team_hint: value,
            },
        });
    }
});

function handleAccountClick() {
    if (!profileEditDisabled.value) {
        profileEdit();
    }
}
</script>

<template>
    <TeamBadge :current-team-name="currentTeamName" />
    <AppTopbarItem
        v-if="isAuthenticated"
        ref="item"
        class="precon-topbar-user-profile"
    >
        <div class="flex">
            <UserAvatar
                data-cy="user-profile-topbar-avatar"
                :loading="accountInfoLoading"
                :user-name="name"
            />
        </div>

        <template #dropdown>
            <li
                class="cursor-pointer"
                data-cy="user-profile-topbar-user-summary"
            >
                <UserAccountSummary
                    :user-name="name"
                    :email="email"
                    :roles="roles"
                    :roles-loading="authorizationLoading"
                    @click="handleAccountClick"
                />
            </li>

            <!-- TODO - use avatar or comparable -->
            <li
                class="precon-topbar-user-dropdown-teams"
                data-cy="user-profile-topbar-teams"
            >
                <h6>{{ t('my-teams') }}</h6>
                <CurrentTeamSelector
                    v-model="switchingTeamId"
                    :teams="teams"
                    :current-team-id="currentTeamId"
                />
            </li>

            <li
                class="cursor-pointer"
                data-cy="user-profile-topbar-logout"
                @click="logout()"
            >
                <i
                    class="pi mr-2 pi-sign-out"
                    :class="{
                        'pi-sign-out ': !logoutLoading,
                        'pi-spinner pi-spin': logoutLoading,
                    }"
                />
                <div>{{ t('logout') }}</div>
            </li>
        </template>
    </AppTopbarItem>

    <AppTopbarItem v-else>
        <LoginButton
            data-cy="user-profile-topbar-login-btn"
            :label="null"
            class="p-button-text"
        />
    </AppTopbarItem>
</template>

<i18n>
    {
        "en": {
            "my-teams": "My Teams",
            "logout": "Logout",
            "currentTeam": "Current team"
        },
        "nl": {
            "my-teams": "Mijn Teams",
            "logout": "Uitloggen",
            "currentTeam": "Huidig team"
        },
        "fr": {
            "my-teams": "Mes équipes",
            "logout": "Déconnecter",
            "currentTeam": "Équipe actuelle"
        }
    }
</i18n>

<style lang="scss">
.current-team-selector {
    display: block !important;
    position: static !important;
    li {
        padding: 0.5rem 2rem 0.5rem 2rem !important;
        cursor: pointer;
    }
}

.precon-topbar-user-dropdown-teams {
    display: block !important;
    border-top: 1px solid var(--surface-border);
    border-bottom: 1px solid var(--surface-border);
    padding: 0 0 4px 0 !important;

    h6 {
        padding: 10px 15px 5px 15px;
        margin-bottom: 0;
    }

    &:hover {
        background: none !important;
    }
}
</style>
